import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, streamPost } from "@/lib/api";
import { fmt, dirColor, biasColor } from "@/lib/format";
import { DashboardLayout } from "@/components/DashboardLayout";
import { DataStatusBadge } from "@/components/DataStatusBadge";
import { PriceChart } from "@/components/PriceChart";
import { Disclaimer } from "@/components/Disclaimer";
import { toast } from "sonner";
import { Bell, ChevronLeft } from "lucide-react";

export default function SignalDetail() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const asset = params.get("asset") || "BTC/USD";
  const tf = params.get("timeframe") || "4h";
  const [sig, setSig] = useState(null);
  const [snap, setSnap] = useState(null);
  const [candles, setCandles] = useState([]);
  const [why, setWhy] = useState("");

  useEffect(() => {
    const enc = encodeURIComponent(asset);
    api.get(`/signals/${enc}?timeframe=${tf}`).then((r) => setSig(r.data)).catch(() => {});
    api.get(`/markets/${enc}/snapshot?timeframe=${tf}`).then((r) => setSnap(r.data)).catch(() => {});
    api.get(`/markets/${enc}/candles?timeframe=${tf}&bars=120`).then((r) => setCandles(r.data.candles)).catch(() => {});
    setWhy("");
    streamPost("/ai/analyst", { symbol: asset, timeframe: tf }, (d) => setWhy((p) => p + d)).catch(() => setWhy("AI explanation temporarily unavailable."));
  }, [asset, tf]);

  const createAlert = async (type) => {
    try { await api.post("/alerts", { symbol: asset, type, channel: "in_app" }); toast.success("Alert created"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!sig) return <DashboardLayout title="Signal"><div className="text-muted-foreground">Loading…</div></DashboardLayout>;

  const factors = snap ? [
    ["Trend", snap.trend, biasColor(snap.trend)],
    ["Momentum (MACD)", snap.macd.hist > 0 ? "Positive" : "Negative", snap.macd.hist > 0 ? "text-bullish" : "text-bearish"],
    ["RSI", `${fmt(snap.rsi, 1)} (${snap.rsi > 70 ? "overbought" : snap.rsi < 30 ? "oversold" : "neutral"})`, ""],
    ["Volatility", `${snap.volatility} (${snap.volatility_pct}%)`, snap.volatility === "high" ? "text-warning" : ""],
    ["Multi-timeframe", sig.mtf_alignment, ""],
  ] : [];

  return (
    <DashboardLayout title="Signal Detail">
      <button onClick={() => navigate(-1)} className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ChevronLeft className="h-4 w-4" /> Back</button>
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          <div className="rounded-2xl border border-border bg-card p-6">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2"><h2 className="font-display text-2xl font-bold">{asset}</h2><DataStatusBadge status={sig.data_status || "DEMO"} /></div>
                <div className="mt-1 text-sm text-muted-foreground">{sig.name} · {tf}</div>
              </div>
              <div className="text-right">
                <div className={`font-display text-xl font-bold ${dirColor(sig.direction)}`}>{sig.direction}</div>
                <div className="text-sm text-muted-foreground">Confidence {sig.confidence}/100 · {sig.quality}</div>
              </div>
            </div>
            <div className="mt-4"><PriceChart candles={candles} height={220} /></div>
          </div>

          <div className="rounded-2xl border border-border bg-card p-6">
            <h3 className="font-display text-lg font-semibold">Why this signal?</h3>
            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              {factors.map(([k, v, c]) => (
                <div key={k} className="flex items-center justify-between rounded-lg border border-border px-3 py-2 text-sm">
                  <span className="text-muted-foreground">{k}</span><span className={`font-medium capitalize ${c}`}>{v}</span>
                </div>
              ))}
            </div>
            <p className="mt-4 whitespace-pre-wrap text-sm leading-relaxed text-muted-foreground" data-testid="signal-why-ai">{why || "Generating AI explanation…"}</p>
          </div>
        </div>

        <div className="space-y-6">
          <div className="rounded-2xl border border-border bg-card p-6">
            <h3 className="font-display text-lg font-semibold">Risk Setup</h3>
            <div className="mt-3 space-y-2 text-sm">
              <Row label="Entry" value={`${sig.entry_low} – ${sig.entry_high}`} />
              <Row label="Stop Loss" value={sig.stop_loss} accent="text-bearish" />
              <Row label="Target 1" value={sig.tp1} accent="text-bullish" />
              <Row label="Target 2" value={sig.tp2} accent="text-bullish" />
              <Row label="Risk / Reward" value={sig.risk_reward} />
              <Row label="Status" value={sig.status || "ACTIVE"} />
            </div>
            <button onClick={() => createAlert(sig.direction.startsWith("BUY") ? "buy_signal" : sig.direction.startsWith("SELL") ? "sell_signal" : "volatility")} data-testid="signal-create-alert" className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-primary py-2.5 text-sm font-semibold text-primary-foreground"><Bell className="h-4 w-4" /> Create Alert</button>
          </div>
          <Disclaimer />
        </div>
      </div>
    </DashboardLayout>
  );
}

function Row({ label, value, accent }) {
  return <div className="flex items-center justify-between"><span className="text-muted-foreground">{label}</span><span className={`font-mono font-medium ${accent || ""}`}>{value}</span></div>;
}
